import * as React from 'react';
import { Select as ChakraSelect, Field, Box, createListCollection } from '@chakra-ui/react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  label?: string;
  error?: string;
  hint?: string;
  placeholder?: string;
  options: SelectOption[];
  containerClassName?: string;
  selectSize?: 'sm' | 'md' | 'lg';
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  searchable?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  id?: string;
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      hint,
      placeholder,
      options = [],
      containerClassName,
      selectSize: _selectSize = 'md',
      required,
      disabled,
      multiple: _multiple = false,
      searchable: _searchable = false,
      value,
      onChange,
      id,
      ...props
    },
    ref
  ) => {
    // Generate ID for accessibility - always call React.useId() to comply with Rules of Hooks
    const generatedId = React.useId();
    const selectId = id || generatedId;

    // Determine field status
    const invalid = !!error;

    // Map sizes
    const _sizeMap = {
      sm: 'sm',
      md: 'md',
      lg: 'lg',
    };

    // Validate and filter options
    const validOptions = options.filter(
      (option) =>
        option &&
        option.value !== undefined &&
        option.value !== null &&
        option.value !== '' &&
        option.label !== undefined &&
        option.label !== null
    );

    // Create collection for Chakra Select v3
    const _collection = createListCollection({
      items: validOptions,
    });

    // Value safety: Only use valid values that exist in options
    const safeValue = React.useMemo(() => {
      if (value === undefined || value === null || value === '') {
        return undefined;
      }
      // Check if value exists in options
      const exists = options.some((option) => option.value === value);
      return exists ? value : undefined;
    }, [value, options]);

    // Filter out invalid props that shouldn't be passed to DOM
    const {
      invalid: _,
      value: _value,
      onChange: _onChange,
      placeholder: _placeholder,
      ..._filteredProps
    } = props as Record<string, unknown>;

    return (
      <Field.Root
        {...(invalid ? { invalid: true } : {})}
        className={containerClassName}
        disabled={disabled}
      >
        {/* Label */}
        {label && (
          <Box
            as="label"
            fontSize="sm"
            fontWeight="medium"
            color={invalid ? '#EF4444' : '#374151'}
            display="block"
            marginBottom="4px"
          >
            {label}
            {required && (
              <Box as="span" color="#EF4444" marginLeft={1}>
                *
              </Box>
            )}
          </Box>
        )}

        {/* Temporary fallback: Native HTML select until zag-js issue is resolved */}
        <Box position="relative">
          <select
            ref={ref}
            id={selectId}
            value={safeValue || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              const newValue = e.target.value;
              onChange?.(newValue);
            }}
            disabled={disabled}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #E2E8F0',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: disabled ? 'not-allowed' : 'pointer',
              ...(disabled && {
                backgroundColor: '#F3F4F6',
                color: '#6B7280',
              }),
            }}
          >
            {placeholder && (
              <option value="" disabled hidden>
                {placeholder}
              </option>
            )}
            {validOptions.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
        </Box>

        {/* Error Message */}
        {error && (
          <Box fontSize="xs" color="#EF4444" marginTop="4px">
            {error}
          </Box>
        )}

        {/* Hint Message */}
        {hint && !error && (
          <Box fontSize="xs" color="#4B5563" marginTop="4px">
            {hint}
          </Box>
        )}
      </Field.Root>
    );
  }
);

Select.displayName = 'Select';

/**
 * @deprecated This component is deprecated since v0.8.72.
 * Use `UnifiedCombobox` or `ComboboxField` from '../index' instead.
 *
 * Migration:
 * - Standalone: `<UnifiedCombobox<string> options={opts} value={v} onChange={fn} />`
 * - React Hook Form: `<ComboboxField control={control} name="field" options={opts} />`
 *
 * @see /docs/planning/combobox/migration-guide.md
 */
// Export composable components for advanced usage
export const SelectRoot = ChakraSelect.Root;
export const SelectControl = ChakraSelect.Control;
export const SelectTrigger = ChakraSelect.Trigger;
export const SelectValueText = ChakraSelect.ValueText;
export const SelectIndicator = ChakraSelect.Indicator;
export const SelectIndicatorGroup = ChakraSelect.IndicatorGroup;
export const SelectClearTrigger = ChakraSelect.ClearTrigger;
export const SelectPositioner = ChakraSelect.Positioner;
export const SelectContent = ChakraSelect.Content;
export const SelectItem = ChakraSelect.Item;
export const SelectItemText = ChakraSelect.ItemText;
export const SelectItemIndicator = ChakraSelect.ItemIndicator;
export const SelectHiddenSelect: React.ComponentType = ChakraSelect.HiddenSelect;

export { Select };
